/* ============================================================================
   djlongbeard.com — global styles (v4)
   Monochromatic neutral chrome (no blue, no gold, one white + a gray ramp).
   The ONLY color comes from the brand-logo squircle tiles. Mobile-first.
   ========================================================================= */

:root {
  --bg: #0b0b0c;
  --bg-deep: #050506;
  --surface-1: #161618;
  --surface-2: #202023;

  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.2);

  --text: #fafafa;
  --text-muted: #8e8e93;
  --text-faint: #82828a; /* placeholders/tertiary — bumped to ~AA on dark */

  --focus: 0 0 0 3px rgba(255, 255, 255, 0.2);
  --active: #5b8def; /* "playing" accent — the ONLY color outside the brand tiles */

  --font-display: 'Fraunces', Georgia, serif;
  --font-ui: 'Inter', system-ui, -apple-system, sans-serif;

  --radius: 14px; /* THE single corner-radius token — used everywhere */
  --squircle: 26%; /* app-icon badge shape (brand logo tiles only) */
  --maxw: 30rem;
  --radio-h: 54px; /* now-playing bar — taller for the mute label + breathing room */
  --pad-x: 1.15rem; /* consistent left/right inset across every section */
  --gap: 0.85rem; /* consistent vertical rhythm between sections */

  color-scheme: dark;
}

* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }

body {
  min-height: 100dvh;
  font-family: var(--font-ui);
  color: var(--text);
  background-color: var(--bg);
  background-image: radial-gradient(140% 120% at 50% -10%, #141416, var(--bg) 55%, var(--bg-deep));
  background-attachment: fixed;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

a { color: inherit; text-decoration: none; }

/* ----------------------------------------------------- pinned top piece */

.topfix {
  position: sticky;
  top: 0;
  z-index: 20;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
}
.topfix__inner { max-width: var(--maxw); margin: 0 auto; padding: 1.1rem var(--pad-x); }

/* ------------------------------------------------------------- radio bar */

.radio {
  position: relative;
  width: 100%;
  background: var(--bg-deep);
  border-bottom: 1px solid var(--border);
  transition: background 0.35s ease, box-shadow 0.35s ease;
}
/* "active" (unmuted) — the bar takes on a blue cast to read as live/playing */
.radio.is-active {
  background: #0b1326;
  box-shadow: inset 0 1px 0 rgba(91, 141, 239, 0.3);
}
.radio__bar {
  max-width: var(--maxw);
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  height: var(--radio-h);
  padding: 0 var(--pad-x);
}

/* always-on equalizer (the stream never stops; mute only silences it) */
.viz {
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
  width: 76px;
  height: 26px;
}
.viz__bar {
  flex: 1;
  max-width: 3.5px;
  height: 100%;
  border-radius: 2px;
  background: var(--text);
  transform: scaleY(0.18);
  transform-origin: center;
  animation-name: vizBar;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  animation-timing-function: ease-in-out;
}
/* each bar rises to its OWN peak (--peak) → a varied, musical spectrum */
@keyframes vizBar { from { transform: scaleY(0.1); } to { transform: scaleY(var(--peak, 1)); } }
/* muted still moves — at a lower peak, so it still reads as "live now" */
@keyframes vizBarLow { from { transform: scaleY(0.1); } to { transform: scaleY(calc(var(--peak, 1) * 0.5)); } }
.viz--live .viz__bar { opacity: 1; background: var(--active); animation-name: vizBar; }
.viz--muted .viz__bar { opacity: 0.6; animation-name: vizBarLow; }

/* now-playing — the track title IS the external YouTube link.
   NOT flex: the title is one continuous inline run so the underline is unbroken. */
.radio__now {
  flex: 1;
  min-width: 0;
  margin: 0;
  position: relative;
  white-space: nowrap;
  color: var(--text);
  text-decoration: none;
}
.radio__now:focus-visible { outline: none; }
.radio__nowtext {
  font-size: 0.84rem;
  text-underline-offset: 3px;
  text-decoration: underline transparent;
  transition: text-decoration-color 0.15s ease;
}
.radio__now:hover .radio__nowtext,
.radio__now:focus-visible .radio__nowtext { text-decoration-color: var(--text); }
.radio__track { font-weight: 600; color: var(--text); }
.radio__by { font-size: 0.95em; color: var(--text-muted); }
.radio__ext {
  display: inline-block;
  vertical-align: -1px;
  margin-left: 5px;
  color: var(--text-muted);
  transition: color 0.15s ease;
}
.radio__now:hover .radio__ext, .radio__now:focus-visible .radio__ext { color: var(--text); }

/* "listen on YouTube" hint — lives ON the song link, sizes to content */
.radio__nowtip {
  position: absolute;
  top: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  width: max-content;
  max-width: 70vw;
  padding: 0.34rem 0.62rem;
  border-radius: var(--radius);
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text);
  font-size: 0.74rem;
  font-weight: 500;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.5);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
  z-index: 30;
}
.radio__nowtip::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: var(--surface-2);
  border-left: 1px solid var(--border);
  border-top: 1px solid var(--border);
}
.radio__now:hover .radio__nowtip,
.radio__now:focus-visible .radio__nowtip { opacity: 1; transform: translateX(-50%); }

/* visually hidden but available to screen readers */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* mute lives on the RIGHT; its tooltip is anchored to it (moves with it,
   sizes to its own content — nothing hardcoded) */
.radio__mutewrap { position: relative; flex: none; display: flex; }
.radio__mute {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 38px;
  padding: 0 0.8rem 0 0.62rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-1);
  color: var(--text);
  font-family: var(--font-ui);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.radio__mute svg { flex: none; }
.radio__mute:hover { background: var(--surface-2); border-color: var(--border-strong); }
.radio__mute:focus-visible { outline: none; box-shadow: var(--focus); }
/* when the stream is active, the mute chip takes the blue aesthetic too */
.radio.is-active .radio__mute { border-color: rgba(91, 141, 239, 0.5); background: rgba(91, 141, 239, 0.12); }
.radio.is-active .radio__mute:hover { background: rgba(91, 141, 239, 0.2); border-color: rgba(91, 141, 239, 0.7); }
.radio.is-active .radio__mute svg { color: var(--active); }
.radio__tip {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  width: max-content;
  max-width: 70vw;
  white-space: nowrap;
  padding: 0.34rem 0.62rem;
  border-radius: var(--radius);
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text);
  font-size: 0.74rem;
  font-weight: 500;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.5);
  z-index: 30;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.radio__tip.is-open { opacity: 1; transform: translateX(-50%); }
.radio__tip::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: var(--surface-2);
  border-left: 1px solid var(--border);
  border-top: 1px solid var(--border);
}

/* ------------------------------------------------------------- social card */

.card { display: flex; align-items: center; gap: 1.1rem; }
.card__avatar {
  width: 76px;
  height: 76px;
  flex: none;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border);
}
.card__body { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
.card__name {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 1.05;
  margin: 0;
  color: var(--text);
}
.card__handle { color: var(--text-muted); font-size: 0.9rem; font-weight: 500; }
.card__quote {
  margin: 0.2rem 0 0;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 0.98rem;
  color: var(--text);
}

/* ----------------------------------------------------------------- link hub */

.page {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 1.4rem var(--pad-x) calc(2.5rem + env(safe-area-inset-bottom));
}

.links { display: flex; flex-direction: column; }
.links__head {
  margin: 0 0 0.9rem;
  padding: 0 0.1rem;
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--text);
}
.links__list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
}

.link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 0.75rem;
  min-height: 66px;
  min-width: 0;
  border-radius: var(--radius);
  background: var(--surface-1);
  border: 1px solid var(--border);
  color: var(--text);
  font-size: 0.98rem;
  font-weight: 500;
  transition: transform 0.16s ease, background 0.16s ease, border-color 0.16s ease, opacity 0.16s ease;
  -webkit-tap-highlight-color: transparent;
}
/* hovering one tile dims the rest */
.links__list:hover .link:not(:hover) { opacity: 0.4; }
.link:hover, .link:focus-visible {
  background: var(--surface-2);
  border-color: var(--border-strong);
  outline: none;
}
.link:focus-visible { box-shadow: var(--focus); }

.link__tile {
  width: 46px;
  height: 46px;
  flex: none;
  border-radius: var(--squircle);
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.35);
}
.link__tile svg { width: 25px; height: 25px; }
.link__label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* hover-revealed "opens in a new tab" cue — a chip with the same background /
   border / radius language as the mute button. Absolutely positioned so it
   never steals width from the label; vertically centered with the text. */
.link__out {
  position: absolute;
  right: 10px;
  top: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: calc(var(--radius) - 4px);
  background: var(--surface-2);
  border: 1px solid var(--border-strong);
  color: var(--text);
  opacity: 0;
  transform: translateY(-50%) translateX(-4px);
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.link:hover .link__out { opacity: 1; transform: translateY(-50%); }
.link__arrow { display: block; }

/* per-platform tagline tooltip — same language as the radio tooltips */
.link:hover { z-index: 5; }
.link__tip {
  position: absolute;
  top: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  width: max-content;
  max-width: min(16rem, 86vw);
  padding: 0.34rem 0.62rem;
  border-radius: var(--radius);
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text);
  font-size: 0.74rem;
  font-weight: 500;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.5);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
  z-index: 40;
}
.link:hover .link__tip, .link:focus-visible .link__tip { opacity: 1; transform: translateX(-50%); }
.link__tip::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: var(--surface-2);
  border-left: 1px solid var(--border);
  border-top: 1px solid var(--border);
}

@media (prefers-reduced-motion: reduce) {
  .link, .link__out, .radio__mute, .radio__yt { transition: none; }
  .viz__bar { animation: none; transform: scaleY(0.5); }
}
